import React, { useState, useEffect } from 'react';
import { Linkedin, Award, Users, TrendingUp, Heart, ChevronDown, ChevronLeft, ChevronRight, Building2, Home, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BackButton from '@/components/BackButton';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const About = () => {
  const [team, setTeam] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [activeYear, setActiveYear] = useState(null);
  const [counters, setCounters] = useState({ projects: 0, team: 0, units: 0, years: 0 });
  const [visibleMilestones, setVisibleMilestones] = useState(new Set());
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const [manualScrollTimer, setManualScrollTimer] = useState(null);
  const [expandedBios, setExpandedBios] = useState({});

  const milestones = [
    {
      year: '2015',
      title: 'Company Created',
      description: 'GN Management was founded with a vision to transform Jersey City\'s urban landscape.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
    },
    {
      year: '2016',
      title: 'First Residential Project in Jersey City',
      description: 'Launched our first residential development, marking the beginning of our journey.',
      image: 'https://customer-assets.emergentagent.com/job_jersey-estates/artifacts/sv09982v_2016.jpg'
    },
    {
      year: '2019',
      title: '3224 Kennedy Blvd (Nanak Niwas)',
      description: '42-unit rental property completed in Journal Square neighborhood.',
      image: 'https://customer-assets.emergentagent.com/job_jersey-estates/artifacts/39wnpvus_2019.jpg'
    },
    {
      year: '2020',
      title: 'Internal Growth',
      description: 'Expanded our team and capabilities to handle larger, more complex projects.',
      image: 'https://customer-assets.emergentagent.com/job_property-mgmt-pro-2/artifacts/vwalrk6w_Internal%20growth%201.png'
    },
    {
      year: '2020',
      title: '50 Projects Milestone',
      description: 'Reached a significant milestone of 50 completed projects across Jersey City.',
      image: 'https://images.unsplash.com/photo-1759104050206-e5ea8dc7ee83?w=800'
    },
    {
      year: '2021',
      title: '161 Van Wagenen Started',
      description: 'Broke ground on 161 Van Wagenen Avenue, a 13-story luxury development.',
      image: 'https://customer-assets.emergentagent.com/job_jersey-estates/artifacts/yabekh0k_2021.jpg'
    },
    {
      year: '2022',
      title: '626 Summit Ave — Singh Tower Started',
      description: 'Commenced construction on our flagship 29-story tower with 202 Luxury units.',
      image: 'https://customer-assets.emergentagent.com/job_jersey-estates/artifacts/d69iteex_2022.jpg'
    },
    {
      year: '2024',
      title: '161 Van Wagenen Completed',
      description: 'Successfully delivered 124 luxury rental units with premium amenities.',
      image: 'https://customer-assets.emergentagent.com/job_jersey-estates/artifacts/rjc88je1_2024.png'
    },
    {
      year: '2024',
      title: 'Singh Tower Tops Off',
      description: 'GN Management proudly celebrated the topping-off of Singh Tower on April 17, a major step forward in Jersey City\'s evolving skyline.',
      image: 'https://customer-assets.emergentagent.com/job_property-mgmt-pro-2/artifacts/3wk9ekp9_topping%20off%20party.png'
    },
    {
      year: '2025',
      title: 'Harborside 9 Marks Next Phase of Growth',
      description: 'GN Management announced the acquisition of the Harborside 9 site on Jersey City\'s waterfront, marking a major step toward a future 57-story residential tower. Planned with hundreds of residences, retail space, and parking, the project reflects a bold vision for the city\'s evolving skyline.',
      image: 'https://customer-assets.emergentagent.com/job_property-mgmt-pro-2/artifacts/8bxphv07_Harborside%209.png'
    },
    {
      year: '2026',
      title: '626 Summit Ave - Singh Tower Opens for Move-Ins',
      description: 'GN Management\'s flagship project, Singh Tower, reached a major milestone in 2026 as residents began moving into the newly completed luxury rental tower.',
      image: 'https://res.cloudinary.com/dzk6j0kr2/image/upload/v1784139110/gn-management/timeline/singh_tower_2026_v2.webp'
    }
  ];

  useEffect(() => {
    fetchTeam();
    animateCounters();
    setupScrollAnimations();
  }, []);

  const toggleBio = (memberId) => {
    setExpandedBios(prev => ({
      ...prev,
      [memberId]: !prev[memberId]
    }));
  };

  const truncateBio = (bio, maxLength = 120) => {
    if (!bio || bio.length <= maxLength) return bio;
    return bio.substring(0, maxLength) + '...';
  };

  // Auto-scroll carousel - advances by 3 slides every 5 seconds
  useEffect(() => {
    if (!isAutoScrollEnabled) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        const nextSlide = prev + 3;
        return nextSlide >= milestones.length ? 0 : nextSlide;
      });
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [isAutoScrollEnabled]);

  const handleManualScroll = (direction) => {
    // Stop auto-scroll
    setIsAutoScrollEnabled(false);

    // Clear existing timer
    if (manualScrollTimer) {
      clearTimeout(manualScrollTimer);
    }

    // Navigate
    if (direction === 'next') {
      setCurrentSlide((prev) => (prev + 1 >= milestones.length ? 0 : prev + 1));
    } else {
      setCurrentSlide((prev) => (prev - 1 < 0 ? milestones.length - 1 : prev - 1));
    }

    // Set timer to resume auto-scroll after 5 seconds of inactivity
    const timer = setTimeout(() => {
      setIsAutoScrollEnabled(true);
    }, 5000);

    setManualScrollTimer(timer);
  };

  const nextSlide = () => handleManualScroll('next');
  const prevSlide = () => handleManualScroll('prev');

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setTeam(response.data);
    } catch (error) {
      console.error('Error fetching team:', error);
    }
  };

  const animateCounters = () => {
    const duration = 2000;
    const steps = 50;
    const increment = duration / steps;

    const targets = {
      projects: 50,
      team: 25,
      units: 1000,
      years: 10
    };

    let current = { projects: 0, team: 0, units: 0, years: 0 };

    const interval = setInterval(() => {
      let allComplete = true;
      Object.keys(targets).forEach(key => {
        if (current[key] < targets[key]) {
          current[key] = Math.min(current[key] + Math.ceil(targets[key] / steps), targets[key]);
          allComplete = false;
        }
      });
      setCounters({ ...current });
      if (allComplete) clearInterval(interval);
    }, increment);
  };

  const setupScrollAnimations = () => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = entry.target.getAttribute('data-milestone-index');
            setVisibleMilestones(prev => new Set([...prev, index]));
          }
        });
      },
      { threshold: 0.2 }
    );

    document.querySelectorAll('[data-milestone-index]').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  };

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="relative py-32 pt-40 bg-gradient-to-br from-gray-900 to-black text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600')] opacity-10 bg-cover bg-center" />
        
        {/* Back Button */}
        <div className="absolute top-24 left-6 z-10">
          <BackButton />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 whitespace-nowrap">About GN Management</h1>
            <p className="text-xl text-gray-300 mb-8">
              A decade of excellence in real estate development, building communities and creating lasting value.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Users, label: 'Projects Completed', value: counters.projects, suffix: '+' },
              { icon: Building2, label: 'Team Members', value: counters.team, suffix: '+' },
              { icon: Building2, label: 'Units Delivered', value: counters.units, suffix: '+' },
              { icon: Calendar, label: 'Years of Excellence', value: counters.years, suffix: '' }
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center scale-in" style={{ animationDelay: `${index * 0.1}s` }}>
                  <Icon className="h-10 w-10 mx-auto mb-4 text-[#20B2AA]" />
                  <div className="text-4xl font-bold text-black mb-2">{stat.value}{stat.suffix}</div>
                  <div className="text-sm text-gray-600 uppercase tracking-wide">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-[#20B2AA]/10 rounded-full">
              <span className="text-[#20B2AA] font-semibold text-sm">Our Story</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-black">Building Communities, Creating Value</h2>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              Founded in 2015, GN Management has grown from a small real estate venture into one of Jersey City's most respected development firms. Our journey has been defined by a commitment to quality, community, and sustainable growth.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              With over 50 completed projects and more than 1,000 units delivered, we've played a significant role in shaping Jersey City's residential landscape. From our first project to our flagship Singh Tower, each development reflects our dedication to excellence and our vision for urban living.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Today, we continue to innovate and expand across the United States, always with an eye toward creating spaces that enhance communities and improve lives. Our team of 25+ professionals brings together decades of combined experience in real estate development, construction management, and property operations.
            </p>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-[#20B2AA]/10 rounded-full">
              <span className="text-[#20B2AA] font-semibold text-sm">Leadership</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-black">Meet Our Team</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Led by visionary entrepreneurs with deep roots in the real estate market.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto justify-items-center">
            {team.map((member, index) => {
              const isExpanded = expandedBios[member.id];
              const hasBio = member.bio && member.bio.length > 0;
              const showExpandButton = hasBio && member.bio.length > 120;
              
              return (
                <div 
                  key={member.id} 
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group w-full max-w-sm"
                >
                  {/* Clear Headshot Image - Taller */}
                  <div className="relative h-96 overflow-hidden">
                    <img 
                      src={member.image_url} 
                      alt={member.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  
                  {/* Bio Below Image */}
                  <div className="p-6">
                    <p className="text-sm font-semibold text-[#20B2AA] mb-1">{member.salutation}</p>
                    <h3 className="text-xl font-bold mb-2 text-gray-900">{member.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">{member.role}</p>
                    
                    {hasBio && (
                      <div>
                        <p className="text-sm text-gray-700 leading-relaxed mb-3">
                          {isExpanded ? member.bio : truncateBio(member.bio)}
                        </p>
                        
                        {showExpandButton && (
                          <button
                            onClick={() => toggleBio(member.id)}
                            className="text-sm font-semibold text-[#20B2AA] hover:text-[#189a93] transition-colors duration-200 flex items-center gap-1"
                          >
                            {isExpanded ? (
                              <>
                                <span>Show Less</span>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                              </>
                            ) : (
                              <>
                                <span>Read Full Bio</span>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Carousel Timeline - Our Journey */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-[#20B2AA]/10 rounded-full">
              <span className="text-[#20B2AA] font-semibold text-sm">Our Journey</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-black">A Decade of Impact</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From humble beginnings to becoming a trusted name in transformative urban development.
            </p>
          </div>
          
          <div className="relative">
            {/* Horizontal Timeline Line */}
            <div className="absolute left-0 right-0 top-6 h-1 bg-gradient-to-r from-[#20B2AA] via-[#189a93] to-[#20B2AA] z-0" />
            
            {/* Carousel Container */}
            <div className="relative overflow-hidden">
              <div 
                className="flex transition-transform duration-700 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 33.333}%)` }}
              >
                {milestones.map((milestone, index) => (
                  <div
                    key={index}
                    data-testid={`milestone-${index}`}
                    className="w-1/3 flex-shrink-0 px-4"
                  >
                    <div className="flex flex-col items-center">
                      {/* Timeline Dot */}
                      <div className="relative z-10 w-12 h-12 rounded-full bg-gradient-to-br from-[#20B2AA] to-[#189a93] flex items-center justify-center shadow-xl border-4 border-white mb-6">
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                      </div>
                      
                      {/* Year Badge */}
                      <div className="mb-6">
                        <div className="bg-gradient-to-r from-[#20B2AA] to-[#189a93] text-white px-6 py-2 rounded-full font-bold text-xl shadow-lg">
                          {milestone.year}
                        </div>
                      </div>
                      
                      {/* Content Card */}
                      <div className="group w-full">
                        <div className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden">
                          {/* Image - Increased Height */}
                          <div className="relative h-96 overflow-hidden">
                            <img
                              src={milestone.image}
                              alt={milestone.title}
                              loading="lazy"
                              className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                          </div>
                          
                          {/* Content */}
                          <div className="p-6">
                            <h3 className="text-xl font-bold mb-3 text-black group-hover:text-[#20B2AA] transition-colors duration-300">
                              {milestone.title}
                            </h3>
                            <p className="text-gray-600 leading-relaxed">
                              {milestone.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white hover:bg-[#20B2AA] text-black hover:text-white rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 z-20 group"
              aria-label="Previous slide"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white hover:bg-[#20B2AA] text-black hover:text-white rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 z-20 group"
              aria-label="Next slide"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          
          {/* Carousel Indicators */}
          <div className="flex justify-center gap-2 mt-8">
            {milestones.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  currentSlide === index 
                    ? 'bg-[#20B2AA] w-8' 
                    : 'bg-gray-300 w-2 hover:bg-gray-400'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-[#20B2AA]/10 rounded-full">
              <span className="text-[#20B2AA] font-semibold text-sm">Our Values</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-black">What Drives Us</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: 'Community First',
                description: 'We build more than buildings - we create spaces that bring people together and strengthen communities.'
              },
              {
                icon: Award,
                title: 'Quality Excellence',
                description: 'Every project meets the highest standards of design, construction, and sustainability.'
              },
              {
                icon: TrendingUp,
                title: 'Long-term Value',
                description: 'We focus on creating lasting value for investors, residents, and the community.'
              }
            ].map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-gray-50 rounded-3xl p-8 hover:shadow-xl transition-shadow duration-300 group">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#20B2AA] to-[#189a93] rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-black">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;